import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { Store } from '@ngrx/store';
import { deleteUndefinedEntriesInPlace, removeFromListByIndex, copyTitle } from '../helpers/helpers';
import { Performer, performerStatus } from '../store/performers.era';
import { ProgramService } from './program.service';
import { SessionInfo, PerformerSessionInfo } from '../store/reselectors/performerSponsors.reselector';
import { EntitiesService } from './entities.service';
import { Ranking } from 'globals/store/events.data';
import { EntityServiceFunctions } from 'globals/store/entity.data';
import { EntityBase } from 'globals/store/entity.shared.data';
import { createId, jsonCopyObject } from 'globals/helpers/helpers';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class PerformersService implements EntityServiceFunctions {

  constructor(
    private eventService: EventService,
    private programService: ProgramService,
    private entitiesService: EntitiesService,
    public store: Store,
  ) {}
  
  addPerformerToMultipleSessions(sessions: SessionInfo[], performerId: string) {
    this.programService.addEntityToMultipleSessions(sessions, performerId, 'performerIds', 'performerStatus', performerStatus.NotInvited)
  }    

  addPerformersToProgramSession(session: SessionInfo, performerIds: string[]) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      performerIds.forEach(performerId => {
        program.sessions_obj[id].performerIds.push(performerId)
        program.sessions_obj[id].performerStatus[performerId] = performerStatus.NotInvited
      })
      console.log({ pIds: program.sessions_obj[id].performerIds })
      this.programService.upsertProgram(program)
    })
  }

  deletePerformerFromSession(session: PerformerSessionInfo | SessionInfo, performerId: string) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      const { performerStatus, performerIds } = program.sessions_obj[id]
      delete performerStatus[performerId]
      removeFromListByIndex(performerIds, performerIds.findIndex(i => performerId === i))
      this.programService.upsertProgram(program)
    })
  }

  changePerformerStatus(sessionId: string, programId: string, status: string, performerId: string) {
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      program.sessions_obj[sessionId].performerStatus[performerId] = status
      this.programService.upsertProgram(program)
    })
  }

  changePerformerBriefStatus(sessionId: string, programId: string, status: string, performerId: string) {
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      const performerBriefStatus = jsonCopyObject(program.sessions_obj[sessionId].performerBriefStatus)
      performerBriefStatus[performerId] = status
      program.sessions_obj[sessionId].performerBriefStatus = performerBriefStatus
      // console.log({ session: program.sessions_obj[sessionId] })
      this.programService.upsertProgram(program)
    })
  }

  getPerformerBase = (partialPerformer:Partial<Performer>):Performer=>({
    id: createId(),
    social: [],
    labels: [],
    name: '',
    ...partialPerformer
  })

  async addPerformer(newPerformer: Performer): Promise<Performer> { // this is to be used for both brand new performers and conversion from old to new
    const performer:Performer = this.getPerformerBase(newPerformer)
    await this.eventService.saveEventPageEntities('performers', [performer])
    return performer
  }

  async addMultiplePerformers(partialPerformers: Partial<Performer[]>): Promise<Performer[]> { // this is to be used for both brand new performers and conversion from old to new
    const newPerformers: Performer[] = partialPerformers.map(this.getPerformerBase)
    await this.eventService.saveEventPageEntities('performers', newPerformers)
    return newPerformers
  }

  async templatePerformers(performers: Performer[]) {
    const performerTemplates = await this.entitiesService.createEntityTemplates('performer', performers)
    const updatedPerformers = performers.map((p, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = performerTemplates[i]
      const updatedPerformer: Performer = { ...p, dateAdded, dateModified, templateId, versionNumber }
      return updatedPerformer
    })
    await this.updatePerformers(updatedPerformers)
  }

  async deletePerformers(performers: Performer[]) {
    await this.eventService.deleteFromEventPages('performers', performers.map(p => p.id))
  }

  async deletePerformer(performer: Performer) {
    await this.deletePerformers([ performer ])
  }

  async updateTypes(performerTypes: Ranking[]) {
    performerTypes.forEach((level, i) => level.rank = performerTypes.length - i)
    const event = await this.eventService.getCurrentEvent()
    await this.eventService.updateEvent(event.eventId, { performerTypes })
  }

  async updateEntities(entities: EntityBase[]) {
    await this.updatePerformers(entities)
  }

  async updatePerformers(updatedPerformers: Performer[]) {
    updatedPerformers.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('performers', updatedPerformers)
  }

  async updatePerformer(performer:Performer) { // Don't use this for multiple performer updates at once, remember they are on event so need to be updated together
    await this.updatePerformers([performer])
  }


  changeShowEditPerformerLinkInPerformerBrief(performer: Performer, includeEditUrl: boolean) {
    performer = jsonCopyObject(performer)
    performer.showEditProfileLinkInPerformerBrief = includeEditUrl
    this.updatePerformer(performer)
  }
  
  async duplicatePerformers(performers: Performer[]) {
    const duplicatedPerformers = performers.map(performer => {
      deleteUndefinedEntriesInPlace(performer)
      const newPerformer = jsonCopyObject(performer)
      newPerformer.name = copyTitle(newPerformer.name, Object.values(performers).map(e => e.name))
      const id = createId()
      delete newPerformer.confirmed
      delete newPerformer.briefed
      newPerformer.id = id
      return newPerformer
    })
    await this.eventService.saveEventPageEntities('performers', duplicatedPerformers)
  }

  
}